import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ReloadIcon, PlusIcon, CheckIcon } from '@radix-ui/react-icons'
import { FaGift } from 'react-icons/fa6'
import useTitle from '../../hooks/useTitle'
import { Button } from '../../components/ui/button'
import axiosInstance from '../../api/axiosInstance'
import { useNavigate, useParams } from 'react-router-dom'
import { generateCardHTML } from '@/config/cardGenerator'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

function BusinessReward() {
  useTitle('Customer Reward')
  const params = useParams()

  const navigate = useNavigate()
  const [data, setData] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [amountToAdd, setAmountToAdd] = useState('')
  const [isAddingProgress, setIsAddingProgress] = useState(false)
  const [isAssigningReward, setIsAssigningReward] = useState(false)
  const [showAddProgressDialog, setShowAddProgressDialog] = useState(false)
  const [showAssignRewardDialog, setShowAssignRewardDialog] = useState(false)

  const baseCardWidth = 700

  const fetchData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await axiosInstance.get('web-api/rewards/card', {
        params: { userCardId: params?.id },
      })
      setData(response.data.data)
    } catch (err) {
      setError(err?.response?.data?.message || 'Error fetching data')
      toast.error(err?.response?.data?.message || 'Error fetching data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddProgress = () => {
    // For visits, we always add exactly 1
    const valueToAdd = data.rewardMechanism === 'number_of_visits' ? 1 : amountToAdd

    if (!valueToAdd || isNaN(valueToAdd) || Number(valueToAdd) <= 0) {
      toast.error('Please enter a valid positive number')
      return
    }

    if (data.rewardStyle === 'stamps' && !Number.isInteger(Number(valueToAdd))) {
      toast.error('Stamps must be whole numbers')
      return
    }

    setShowAddProgressDialog(true)
  }

  const confirmAddProgress = async () => {
    setShowAddProgressDialog(false)
    setIsAddingProgress(true)

    try {
      const valueToAdd = data.rewardMechanism === 'number_of_visits' ? 1 : Number(amountToAdd)
      const resp = await axiosInstance.post('/web-api/rewards/add-progress', {
        progressAmount: valueToAdd,
        userCardId: data.userCardId,
      })
      toast.success(resp.data.message || 'Progress added successfully!')
      fetchData()
      setAmountToAdd('')
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error adding progress')
    } finally {
      setIsAddingProgress(false)
    }
  }

  const handleAssignReward = () => {
    setShowAssignRewardDialog(true)
  }

  const confirmAssignReward = async () => {
    setShowAssignRewardDialog(false)
    setIsAssigningReward(true)

    try {
      await axiosInstance.get('web-api/rewards/assign', {
        params: { userCardId: data.userCardId },
      })
      toast.success('Reward assigned successfully!')
      fetchData()
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error assigning reward')
    } finally {
      setIsAssigningReward(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ReloadIcon className="w-8 h-8 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">
        <p>{error}</p>
        <Button onClick={fetchData} className="mt-4">
          Retry
        </Button>
      </div>
    )
  }

  // Calculate extra progress that will carry over
  const currentProgress = data.rewardStyle === 'stamps' ? data.stampsFilled : data.progressValue
  const newTotal =
    currentProgress + (data.rewardMechanism === 'number_of_visits' ? 1 : Number(amountToAdd))
  const extraProgress = newTotal > data.threshold ? newTotal - data.threshold : 0

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6 bg-gray-50">
      <div
        className={`flex justify-end p-1 cursor-pointer transition-colors text-blue-500 w-fit`}
        onClick={() => navigate(-1)}
      >
        <span>Back to Customers</span>
      </div>
      {/* Confirmation Dialogs */}
      <Dialog open={showAddProgressDialog} onOpenChange={setShowAddProgressDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Progress Addition</DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                Are you sure you want to add{' '}
                {data.rewardMechanism === 'number_of_visits' ? 1 : amountToAdd}{' '}
                {data.rewardStyle === 'stamps' ? 'stamps' : 'progress '}?
              </p>

              {data.rewardStyle === 'stamps' ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: data.threshold }).map((_, index) => (
                      <div
                        key={index}
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                          index < data.stampsFilled
                            ? 'bg-green-500 border-green-600'
                            : index < newTotal
                              ? 'bg-blue-500 border-blue-600'
                              : 'bg-white border-gray-300'
                        }`}
                      >
                        {index < data.stampsFilled && <CheckIcon className="w-5 h-5 text-white" />}
                        {index >= data.stampsFilled && index < newTotal && (
                          <PlusIcon className="w-4 h-4 text-white" />
                        )}
                      </div>
                    ))}
                  </div>
                  {extraProgress > 0 && (
                    <div className="bg-blue-50 p-2 rounded text-sm">
                      <p className="font-medium text-blue-700">
                        {extraProgress} stamp{extraProgress > 1 ? 's' : ''} will carry over to next
                        reward cycle
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Current Progress:</span>
                    <span className="font-semibold">{data.progressValue}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Adding:</span>
                    <span className="font-semibold">
                      +{data.rewardMechanism === 'number_of_visits' ? 1 : amountToAdd}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm font-bold mt-2">
                    <span>New Total:</span>
                    <span>{newTotal}</span>
                  </div>
                  {extraProgress > 0 && (
                    <div className="bg-blue-50 p-2 rounded text-sm mt-2">
                      <p className="font-medium text-blue-700">
                        {extraProgress} progress will carry over to next reward cycle
                      </p>
                    </div>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProgressDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmAddProgress} className="bg-blue-600 hover:bg-blue-700">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAssignRewardDialog} onOpenChange={setShowAssignRewardDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Reward Assignment</DialogTitle>
            <DialogDescription>
              <div className="bg-green-50 rounded-lg p-3 mb-3">
                <p className="font-medium text-green-700">This card has reached its threshold!</p>
                <p className="mt-1 text-sm">
                  Assigning a reward will reset this card and create a new reward cycle with any
                  extra progress.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-gray-50 p-2 rounded">
                  <p className="text-xs text-gray-500">Target</p>
                  <p className="font-bold">{data.threshold}</p>
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <p className="text-xs text-gray-500">Current Progress</p>
                  <p className="font-bold">{currentProgress}</p>
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <p className="text-xs text-blue-500">Extra Progress</p>
                  <p className="font-bold text-blue-700">{data.extraProgressForNextCycle}</p>
                </div>
                <div className="bg-purple-50 p-2 rounded">
                  <p className="text-xs text-purple-500">New Progress</p>
                  <p className="font-bold text-purple-700">{data.extraProgressForNextCycle}</p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignRewardDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmAssignReward}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
            >
              Assign Reward
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Reward Details Card */}
          <div className="bg-white rounded-lg shadow-sm border border-emerald-100 p-6">
            <h2 className="text-xl font-semibold text-teal-800 mb-4">Reward Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-emerald-200 rounded-lg p-3 bg-white bg-opacity-50">
                <p className="text-sm text-emerald-600">Reward</p>
                <p className="text-lg font-semibold text-teal-800">
                  {data.rewardType === 'discount'
                    ? `${data.rewardQty}% discount`
                    : data.rewardType === 'points'
                      ? `${data.rewardQty} points`
                      : `${data.rewardQty} free item${data.rewardQty > 1 ? 's' : ''}`}
                </p>
              </div>
              <div className="border border-teal-200 rounded-lg p-3 bg-white bg-opacity-50">
                <p className="text-sm text-teal-600">Style</p>
                <p className="text-lg font-semibold text-teal-800 capitalize">
                  {data.rewardStyle === 'stamps' ? data.rewardStyle : 'Progress bar'}
                </p>
              </div>
              <div className="border border-green-200 rounded-lg p-3 bg-white bg-opacity-50">
                <p className="text-sm text-green-600">Target</p>
                <p className="text-lg font-semibold text-green-800">{data.threshold}</p>
              </div>
              <div className="border border-cyan-200 rounded-lg p-3 bg-white bg-opacity-50">
                <p className="text-sm text-cyan-600">Based On</p>
                <p className="text-lg font-semibold text-cyan-800 capitalize">
                  {data.rewardMechanism === 'amount_spent'
                    ? 'Amount spent'
                    : data.rewardMechanism === 'quantity_purchased'
                      ? 'Items purchased'
                      : 'Number of visits'}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Details Card */}
          <div className="bg-white rounded-lg shadow-sm border border-blue-100 p-6">
            <h2 className="text-xl font-semibold text-indigo-800 mb-4">Customer Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-indigo-600">Name</p>
                <p className="font-medium text-gray-800">{data.userDetails?.name || 'N/A'}</p>
              </div>
              <div className="">
                <p className="text-sm text-indigo-600">Customer ID</p>
                <p className="font-medium text-gray-800">{data.customerId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-indigo-600">Contact</p>
                <p className="font-medium text-gray-800">
                  {data.userDetails?.country_code && (
                    <span className="text-gray-500">{data.userDetails.country_code} </span>
                  )}
                  {data.userDetails?.mobile || 'N/A'}
                </p>
              </div>
              <div className="">
                <p className="text-sm text-indigo-600">Email</p>
                <p className="font-medium text-gray-800">{data.userDetails?.email || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Business Stats Card */}
          <div className="bg-white rounded-lg shadow-sm border border-purple-100 p-6">
            <h2 className="text-xl font-semibold text-purple-800 mb-4">Customer Statistics</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-purple-200 rounded-lg p-3 bg-white bg-opacity-50">
                <p className="text-sm text-purple-600">Total Rewards</p>
                <p className="text-2xl font-bold text-purple-800">
                  {data.businessStats?.totalRewards || 0}
                </p>
              </div>
              <div className="border border-blue-200 rounded-lg p-3 bg-white bg-opacity-50">
                <p className="text-sm text-blue-600">Total Spent</p>
                <p className="text-2xl font-bold text-blue-800">
                  {data.businessStats?.totalAmountSpent || 0}
                </p>
              </div>
              <div className="border border-indigo-200 rounded-lg p-3 bg-white bg-opacity-50">
                <p className="text-sm text-indigo-600">Total Visits</p>
                <p className="text-2xl font-bold text-indigo-800">
                  {data.businessStats?.totalVisits || 0}
                </p>
              </div>
              <div className="border border-violet-200 rounded-lg p-3 bg-white bg-opacity-50">
                <p className="text-sm text-violet-600">Items Purchased</p>
                <p className="text-2xl font-bold text-violet-800">
                  {data.businessStats?.totalItemsPurchased || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Card Preview */}
          <div className="bg-white rounded-lg shadow-sm border border-amber-100 p-6">
            <div className="flex justify-center">
              <div className="relative w-[300px] md:w-[490px] lg:w-[490px] h-[160px] md:h-[180px] lg:h-[210px] flex items-center justify-center">
                {Object.keys(data?.card || {}).length > 0 && (
                  <div
                    className="transform scale-[0.46] md:scale-[0.5] lg:scale-[0.6]"
                    style={{ width: `${baseCardWidth}px`, height: '350px' }}
                    dangerouslySetInnerHTML={{ __html: generateCardHTML(data?.card) }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Progress Tracking */}
          <div className="bg-white rounded-lg shadow-sm border border-fuchsia-100 p-6">
            <h2 className="text-xl font-semibold text-fuchsia-800 mb-4">Progress Tracking</h2>
            {data.rewardStyle === 'stamps' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-fuchsia-700">Stamps Collected</p>
                  <p className="font-bold text-fuchsia-800">
                    {data.stampsFilled} / {data.threshold}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: data.threshold }).map((_, index) => (
                    <div
                      key={index}
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                        index < data.stampsFilled
                          ? 'bg-gradient-to-br from-pink-500 to-rose-500 border-rose-600 shadow-sm'
                          : 'bg-white border-fuchsia-200'
                      }`}
                    >
                      {index < data.stampsFilled && <CheckIcon className="w-6 h-6 text-white" />}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-fuchsia-700">Progress</p>
                  <p className="font-bold text-fuchsia-800">
                    {data.progressValue} / {data.threshold} ({data.progressPercentage}%)
                  </p>
                </div>
                <div className="w-full bg-fuchsia-100 rounded-full h-4">
                  <div
                    className="bg-gradient-to-r from-fuchsia-500 to-pink-500 h-4 rounded-full shadow-sm"
                    style={{ width: `${data.progressPercentage}%` }}
                  ></div>
                </div>
                <p className="text-sm text-fuchsia-600">
                  {data.threshold - data.progressValue} more to reach the reward target
                </p>
              </div>
            )}
          </div>

          {/* Action Section */}
          <div className="bg-white rounded-lg shadow-sm border border-sky-100 ">
            {!data.isFilled ? (
              <div className="p-6 space-y-4">
                <h2 className="text-xl font-semibold text-sky-800 mb-4">Add Progress</h2>
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-sky-700 mb-1">
                      {data.rewardMechanism === 'number_of_visits'
                        ? 'Visit to add'
                        : data.rewardStyle === 'stamps'
                          ? 'Stamps to add'
                          : 'Progress to add'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      step={data.rewardStyle === 'stamps' ? '1' : 'any'}
                      value={data.rewardMechanism === 'number_of_visits' ? 1 : amountToAdd}
                      onChange={(e) => {
                        if (data.rewardMechanism !== 'number_of_visits') {
                          setAmountToAdd(e.target.value)
                        }
                      }}
                      disabled={data.rewardMechanism === 'number_of_visits'}
                      className="w-full px-4 py-2 border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-300 focus:border-sky-400 disabled:bg-sky-100 disabled:cursor-not-allowed text-sky-800 placeholder-sky-300"
                      placeholder={
                        data.rewardStyle === 'stamps' ? 'Number of stamps' : 'Progress amount'
                      }
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleAddProgress}
                      disabled={isAddingProgress}
                      className="w-full md:w-auto bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-md transition-all"
                    >
                      {isAddingProgress ? (
                        <ReloadIcon className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <PlusIcon className="w-4 h-4 mr-2" />
                      )}
                      Add Progress
                    </Button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-sky-600">
                  {data.rewardStyle === 'stamps'
                    ? 'Add stamps to this loyalty card. Extra stamps will carry over to the next reward cycle.'
                    : 'Add progress value to this loyalty card. Extra progress will carry over to the next reward cycle.'}
                </p>
              </div>
            ) : (
              <div className="rounded-lg p-4 bg-gradient-to-r from-green-50 to-emerald-100 border border-emerald-200 shadow-inner">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-emerald-800 mb-1">Reward Status</h3>
                    <p className="text-emerald-700">
                      This loyalty card is ready for reward assignment!
                    </p>
                  </div>
                  <Button
                    onClick={handleAssignReward}
                    disabled={isAssigningReward}
                    className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-md transition-all"
                  >
                    {isAssigningReward ? (
                      <ReloadIcon className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FaGift className="w-4 h-4 mr-2" />
                    )}
                    Assign Reward
                  </Button>
                </div>
                <div className="mt-3 p-3 bg-white bg-opacity-70 border border-emerald-200 rounded-lg">
                  <p className="text-sm text-emerald-600">
                    After assigning this reward, the customer can view its details in their card
                    rewards. A new reward cycle will automatically begin with the remaining progress
                    ({data.extraProgressForNextCycle}) or start from zero if there's no extra
                    progress.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BusinessReward
